import axios from 'axios';
import { translateApiError } from '@/lib/errorMessages';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to extract error message from API response
// If t() function provided, translates error to user's language
export const getErrorMessage = (error, fallback = 'An error occurred', t = null) => {
  if (t) return translateApiError(error, t, fallback);
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return fallback;
};

// Create axios instance with interceptors
const api = axios.create({
  baseURL: API,
});

export const authHeaders = () => {
  const token = localStorage.getItem('workplus_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('workplus_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || '';
    const status = error.response?.status;
    const detail = error.response?.data?.detail || '';
    
    if (status === 401 && !url.includes('/auth/login')) {
      localStorage.removeItem('workplus_token');
      const isExpired = detail.toLowerCase().includes('expired');
      window.dispatchEvent(new CustomEvent('workplus:unauthorized', {
        detail: { reason: isExpired ? 'session_expired' : 'unauthorized' }
      }));
    }
    
    if (status === 429) {
      window.dispatchEvent(new CustomEvent('workplus:rate_limited', {
        detail: { message: detail }
      }));
    }
    
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
};

// Users
export const usersAPI = {
  list: (params) => api.get('/users', { params }),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  bulkImport: (employees) => api.post('/users/bulk-import', employees),
  bulkImportCSV: (csvContent, defaultPassword = 'changeme123') => 
    api.post('/users/bulk-import-csv', { csv_content: csvContent, default_password: defaultPassword }),
};

// Objects
export const objectsAPI = {
  list: (params) => api.get('/objects', { params }),
  get: (id) => api.get(`/objects/${id}`),
  create: (data) => api.post('/objects', data),
  update: (id, data) => api.put(`/objects/${id}`, data),
  updateGeofence: (id, data) => api.put(`/objects/${id}/geofence`, data),
  delete: (id) => api.delete(`/objects/${id}`),
};

// Status
export const statusAPI = {
  current: () => api.get('/status/current'),
  update: (data) => api.post('/status/update', data),
  myObjects: () => api.get('/status/my-objects'),
  todayLog: () => api.get('/status/today-log'),
  dayLog: (date) => api.get('/status/today-log', { params: { date } }),
};

// Live View
export const liveAPI = {
  object: (objectId) => api.get(`/live/${objectId}`),
  all: () => api.get('/live'),
};

// Reports
export const reportsAPI = {
  daily: (filters) => api.post('/reports/daily', filters),
  myHours: (startDate, endDate) => api.get('/reports/my-hours', { 
    params: { start_date: startDate, end_date: endDate } 
  }),
  exportTimesheet: (year, month, objectIds, userIds, lang = 'et') => {
    const params = { year, month, lang };
    if (objectIds) params.object_ids = objectIds;
    if (userIds) params.user_ids = userIds;
    return api.get('/reports/timesheet-export', { 
      params, 
      responseType: 'blob' 
    });
  },
  exportTimesheetExcel: (year, month, objectId, applyShiftRules = true, lang = 'et') => {
    const params = { year, month, apply_shift_rules: applyShiftRules, lang };
    if (objectId && objectId !== 'all-objects') params.object_id = objectId;
    return api.get('/reports/timesheet-excel', { 
      params, 
      responseType: 'blob' 
    });
  }
};

// Violations
export const violationsAPI = {
  list: (params) => api.get('/violations', { params }),
  my: (params) => api.get('/violations/my', { params }),
  delete: (id) => api.delete(`/violations/${id}`),
  update: (id, data) => api.put(`/violations/${id}`, null, { params: data }),
};

// Month Closing
export const monthsAPI = {
  close: (year, month) => api.post('/months/close', { year, month }),
  reopen: (year, month) => api.post('/months/reopen', { year, month }),
  status: (year) => api.get('/months/status', { params: { year } }),
};

// Billing
export const billingAPI = {
  report: (year, month) => api.get(`/billing/${year}/${month}`),
};

// Audit
export const auditAPI = {
  list: (params) => api.get('/audit', { params }),
};

// Employee Notes
export const notesAPI = {
  // Unified feed for admin/hr viewing an employee
  feed: (employeeId, skip = 0, limit = 10) => api.get(`/notes/feed/${employeeId}`, { params: { skip, limit } }),
  // Unified feed for the current employee (own dashboard)
  myFeed: (skip = 0, limit = 10) => api.get('/notes/feed/me', { params: { skip, limit } }),
  // Create note (admin → employee)
  create: (data) => api.post('/notes', data),
  // Create self-note (employee)
  createSelf: (data) => api.post('/notes/my', data),
  // Delete any note (checks permissions on backend)
  delete: (noteId) => api.delete(`/notes/unified/${noteId}`),
  // Clear all notes for employee (admin/hr only)
  clearAll: (employeeId) => api.delete(`/notes/employee/${employeeId}/all`),
  // Upload photo to self-note
  uploadPhoto: (noteId, file, photoTakenAt) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = photoTakenAt ? `?photo_taken_at=${encodeURIComponent(photoTakenAt)}` : '';
    return api.post(`/notes/my/${noteId}/attachments${params}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Legacy — keep for admin pages that still use old endpoints
  list: (employeeId) => api.get(`/notes/employee/${employeeId}`),
  update: (noteId, data) => api.put(`/notes/${noteId}`, data),
};

export const selfNotesAPI = notesAPI; // alias for backwards compat

// Absences
export const absencesAPI = {
  list: (params) => api.get('/absences', { params }),
  create: (data) => api.post('/absences', data),
  update: (absenceId, data) => api.put(`/absences/${absenceId}`, data),
  delete: (absenceId) => api.delete(`/absences/${absenceId}`),
};

// Demo Data
export const seedDemoData = () => api.post('/seed-demo-data');

// Shifts / Schedule
export const shiftsAPI = {
  list: (month, year, objectId) => api.get('/shifts', { params: { month, year, ...(objectId ? { object_id: objectId } : {}) } }),
  my: (month, year) => api.get('/shifts/my', { params: { month, year } }),
  create: (data) => api.post('/shifts', data),
  bulk: (assignments) => api.post('/shifts/bulk', { assignments }),
  update: (id, data) => api.put(`/shifts/${id}`, data),
  delete: (id) => api.delete(`/shifts/${id}`),
  templates: () => api.get('/shifts/templates'),
  createTemplate: (data) => api.post('/shifts/templates', data),
  deleteTemplate: (id) => api.delete(`/shifts/templates/${id}`),
};

export default api;
